import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X, ArrowUpRight, ArrowRight } from "lucide-react";
import { SectionReveal, StaggerWords } from "../components/Motion";
import ResponsiveServiceImage from "../components/ResponsiveServiceImage";
import { useT } from "../i18n/LanguageContext";

const ServiceDialog = ({ service, onClose }) => {
  const { t } = useT();
  const navigate = useNavigate();
  React.useEffect(() => {
    if (!service) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [service, onClose]);

  return (
    <AnimatePresence>
      {service && (
        <motion.div
          data-testid="service-dialog"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
          className="fixed inset-0 z-[100] bg-[#4c535a]/50 backdrop-blur-sm flex items-center justify-center"
          onClick={onClose}
        >
          {/* Panel: full-screen sheet on mobile (100svh), contained ≤90svh panel on tablet/desktop.
              Height/max-height come from the .service-panel CSS class. Content scrolls inside. */}
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="service-panel relative bg-[#EFECE6] w-full max-w-[1200px] md:m-6 flex flex-col overflow-hidden"
          >
            {/* Header row in normal flow: close control always visible at the top */}
            <div className="flex-none flex items-center justify-between gap-4 px-5 py-4 md:px-8 border-b border-[#4c535a]/15">
              <p className="text-[11px] uppercase tracking-[0.28em] text-[#4c535a]">
                {t.servicesPage.service_label} · {service.number}
              </p>
              <button
                data-testid="dialog-close"
                onClick={onClose}
                className="flex-none w-10 h-10 flex items-center justify-center border border-[#4c535a]/40 hover:bg-[#4c535a] hover:text-[#EFECE6] transition-colors duration-300"
                aria-label="Close"
              >
                <X className="w-4 h-4" strokeWidth={1.4} />
              </button>
            </div>

            {/* Scroll region: image above text (mobile/tablet), beside text (desktop 1200px+). Never behind text. */}
            <div className="flex-1 overflow-y-auto">
              <div className="grid grid-cols-1 xl:grid-cols-2">
                <ResponsiveServiceImage
                  slug={service.image}
                  alt={service.name}
                  testId={`dialog-image-${service.image}`}
                  className="w-full"
                />
                <div className="p-8 md:p-12 lg:p-14">
                  <h2 className="type-h1 text-[#4c535a]">
                    {service.name}
                  </h2>
                  <p className="mt-4 type-lead italic text-[#4c535a] leading-snug">{service.tagline}</p>

                  <div className="mt-8">
                    <p className="service-label mb-3">{t.servicesPage.what_we_do}</p>
                    <p className="service-body">{service.what_we_do}</p>
                  </div>

                  <ul className="mt-6 space-y-3">
                    {service.bullets.map((b) => (
                      <li key={b} className="service-body flex items-start gap-3">
                        <span className="mt-1.5 block w-3 h-px bg-[#4c535a] flex-none" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="mt-8 pt-6 border-t border-[#4c535a]/20">
                    <p className="service-label mb-1">{t.servicesPage.format}</p>
                    <p className="service-body">{service.format}</p>
                  </div>

                  <div className="mt-6">
                    <p className="service-label mb-2">{t.servicesPage.leave_with}</p>
                    <p className="service-body">{service.what_you_leave_with}</p>
                  </div>

                  {/* Mobile: short label, 0.75rem / 0.12em so it fits one line. Tablet+: full label. */}
                  <button
                    data-testid={`dialog-enquire-${service.id}`}
                    onClick={() => {
                      onClose();
                      navigate(`/enquiry?service=${service.id}`);
                    }}
                    className="group mt-10 inline-flex items-center gap-3 px-6 py-4 bg-[#4c535a] text-[#EFECE6] uppercase hover:bg-[#3d434a] transition-colors duration-500 w-full justify-center md:w-auto whitespace-nowrap text-[0.75rem] tracking-[0.12em] md:text-[11px] md:tracking-[0.24em]"
                  >
                    <span className="md:hidden">{t.servicesPage.dialog_enquire_short}</span>
                    <span className="hidden md:inline">{t.servicesPage.dialog_enquire(service.name)}</span>
                    <ArrowRight className="w-4 h-4 flex-none transition-transform duration-500 group-hover:translate-x-1" strokeWidth={1.4} />
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const ServiceCard = ({ s, i, onOpen }) => {
  const isTall = i % 3 === 0;
  return (
    <SectionReveal delay={i * 0.04} className={`col-span-12 ${isTall ? "md:col-span-7" : "md:col-span-5"}`}>
      <button
        onClick={() => onOpen(s)}
        data-testid={`service-card-${s.id}`}
        id={s.id}
        className="group text-left w-full block"
      >
        {/* Image stands alone — text never sits on top of it */}
        <div
          className="relative overflow-hidden mx-auto md:mx-0"
          style={{
            maxWidth: "520px",
            maxHeight: "min(70vh, 65svh)",
          }}
        >
          <ResponsiveServiceImage
            slug={s.image}
            alt={s.name}
            priority={i === 0}
            testId={`service-image-${s.id}`}
            className="transition-transform duration-[1400ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.05]"
          />
          <div className="absolute inset-0 pointer-events-none border border-[#4c535a]/10" />
          <div className="absolute top-5 right-5 w-10 h-10 border border-[#4c535a]/40 bg-[#EFECE6]/85 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500">
            <ArrowUpRight className="w-4 h-4 text-[#4c535a]" strokeWidth={1.4} />
          </div>
        </div>
        <div className="pt-5">
          <p className="text-[10px] uppercase tracking-[0.28em] text-[#4c535a]/60 mb-2">No. {s.number}</p>
          <h3 className="type-h1 text-[#4c535a]">
            {s.name}
          </h3>
          <div className="mt-3 grid grid-cols-12 gap-4">
            <p className="col-span-12 md:col-span-8 text-[14px] leading-[1.65] text-[#4c535a]/80 max-w-[52ch]">
              {s.tagline}
            </p>
            <p className="col-span-12 md:col-span-4 md:text-right text-[11px] uppercase tracking-[0.28em] text-[#4c535a]">
              {s.format}
            </p>
          </div>
        </div>
      </button>
    </SectionReveal>
  );
};

export default function ServicesPage() {
  const { t } = useT();
  const [open, setOpen] = React.useState(null);
  const firstSlug = t.services[0]?.image;

  // Preload the first (above-the-fold) service image for LCP; cleaned up on unmount.
  React.useEffect(() => {
    if (!firstSlug) return;
    const build = (ext) =>
      [480, 800, 1200, 1600].map((w) => `/services/${firstSlug}-${w}.${ext} ${w}w`).join(", ");
    const links = ["avif", "webp"].map((ext) => {
      const l = document.createElement("link");
      l.rel = "preload";
      l.as = "image";
      l.type = `image/${ext}`;
      l.href = `/services/${firstSlug}-800.${ext}`;
      l.imageSrcset = build(ext);
      l.imageSizes = "(max-width: 767px) 92vw, 42vw";
      l.setAttribute("fetchpriority", "high");
      document.head.appendChild(l);
      return l;
    });
    return () => links.forEach((l) => l.remove());
  }, [firstSlug]);

  React.useEffect(() => {
    const hash = window.location.hash.replace("#", "");
    if (hash) {
      const s = t.services.find((x) => x.id === hash);
      if (s) setTimeout(() => setOpen(s), 500);
    }
  }, [t.services]);

  return (
    <div className="pt-32 md:pt-40 pb-24 md:pb-32 bg-[#EFECE6]" data-testid="services-page">
      <div className="max-w-[1600px] mx-auto px-5 md:px-10">
        <div className="grid grid-cols-12 gap-6 mb-16 md:mb-24">
          <p className="col-span-12 md:col-span-3 text-[11px] uppercase tracking-[0.28em] text-[#4c535a]">{t.servicesPage.eyebrow}</p>
          <h1 className="col-span-12 md:col-span-9 type-display text-[#4c535a]">
            <StaggerWords text={t.servicesPage.title_1} />
            <br />
            <StaggerWords text={t.servicesPage.title_2} delay={0.15} className="italic font-light" />
          </h1>
        </div>

        <div className="grid grid-cols-12 gap-5 md:gap-12">
          {t.services.map((s, i) => (
            <ServiceCard key={s.id} s={s} i={i} onOpen={setOpen} />
          ))}
        </div>

        <div className="mt-24 md:mt-40 border-t border-[#4c535a]/25 pt-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <h3 className="type-h1 text-[#4c535a]">
            {t.servicesPage.unsure}
          </h3>
          <Link to="/enquiry" data-testid="services-cta-enquire" className="group inline-flex items-center gap-3 px-6 py-4 bg-[#4c535a] text-[#EFECE6] text-[11px] uppercase tracking-[0.24em] hover:bg-[#3d434a] transition-colors duration-500 flex-none">
            {t.common.begin_enquiry}
            <ArrowRight className="w-4 h-4 transition-transform duration-500 group-hover:translate-x-1" strokeWidth={1.4} />
          </Link>
        </div>
      </div>

      <ServiceDialog service={open} onClose={() => setOpen(null)} />
    </div>
  );
}
